import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import { PlacementFrontend } from '@post-office/browser--post-office-team/rovo-nudge';

import type { MessageTemplateContext } from '../../../index';

import { NudgeDefinition } from '@post-office/browser--post-office-team/rovo-nudge/nudge-definition';

import AiGenerativeRemoveSilenceIcon from '@atlaskit/icon-lab/core/ai-generative-remove-silence';

import { useCallback } from 'react';

import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';

import { useClickedRovoNudgeDismiss, useClickedRovoNudgeCta } from '@post-office/rovo-nudge-util';

import { defineMessages, useIntl } from 'react-intl';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

const messages = defineMessages({
	title: {
		id: 'post-office.growth.jira-board-catch-up-rovo-nudge.title',
		defaultMessage: 'Catch up on recent changes',
		description: 'Title of the Rovo nudge for Jira board catch-up',
	},
	secondaryTitle: {
		id: 'post-office.growth.jira-board-catch-up-rovo-nudge.secondary-title',
		defaultMessage: 'Get a quick summary of new issues and important activity.',
		description: 'Secondary title of the Rovo nudge for Jira board catch-up',
	},
	staticPrompt: {
		id: 'post-office.growth.jira-board-catch-up-rovo-nudge.static-prompt',
		defaultMessage: `Summarize the most recent activity on this Jira board. Create an executive-ready update (max ~8 bullets total) using this structure: 1. Sprint / timeline context
- Current sprint or time window, dates, and high-level status (e.g. "on track / at risk / off track"). 2. Key progress / wins (2-4 bullets)
- Focus on issues that moved into In Progress, In Review / Pending Review, or Done in the selected period.
- For each, include: issue key, short description (3-7 words), and current status only (no technical deep dive). 3. Blockers / risks (1-3 bullets)
- Call out any flagged issues, long-running items stuck in In Progress or In Review, or dependencies that could impact delivery for this sprint / period. 4. Scope & pipeline (1-3 bullets)
- Highlight important newly created or To Do issues that are not yet started but are significant for upcoming milestones.
- Note if work is skewed toward setup/foundations vs user-visible outcomes. 5. Next-focus recommendation (1-2 bullets)
- Recommend the 1-2 highest-leverage next actions to keep the team's objectives on track, referencing specific issue keys. Output the answer as concise markdown bullets, optimized for an executive audience (outcomes, risks, and next moves over implementation details).`,
		description: 'Static prompt sent to Rovo for Jira board catch-up summary',
	},
});

const nudgeContext = {
	id: 'jira-board-catch-up-rovo-nudge',
};

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<{ title: string; description: string }>(),
		Component: ({ description }: MessageTemplateContext) => {
			const intl = useIntl();
			useClickedRovoNudgeCta({ nudgeId: nudgeContext.id });
			useClickedRovoNudgeDismiss({ nudgeId: nudgeContext.id });

			const icon = useCallback(
				() => <AiGenerativeRemoveSilenceIcon size="medium" label="Jira board summary" />,
				[],
			);

			return (
				<NudgeDefinition
					title={intl.formatMessage(messages.title)}
					secondaryTitle={intl.formatMessage(messages.secondaryTitle)}
					description={description}
					icon={icon}
					handleType={HandleActionType.Chat}
					staticPrompt={intl.formatMessage(messages.staticPrompt)}
					starterType="static"
					nudgeContext={nudgeContext}
				/>
			);
		},
	},
});
